import { useTransactionTranslations } from '@/hooks/useTransactionTranslations.tsx';
import { useTenantID } from '@/hooks/useTenantID.tsx';
import { formatDate } from '@/helpers/formatting';
import Button from '../core/Button';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMemo } from 'react';

export interface TransactionListItemData {
  id: string;
  transaction_product_id: string;
  created_at: string;
  title: string;
  subtitle?: string;
  count: number;
  source: string;
  amount_inc_vat: number;
  amount_ex_vat: number;
  vat_amount: number;
  state: string;
  department_name?: string;
  organization_name?: string;
  refund_transaction_id?: string;
  bobcat_order_id?: string;
  bobcat_transaction_id?: string;
}

interface Props {
  transaction: TransactionListItemData;
}

export const TransactionListItem = ({ transaction }: Props) => {
  const tenantId = useTenantID();
  const { formatMessage, locale } = useIntl();
  const { getSourceTranslation, getStateTranslation } =
    useTransactionTranslations();

  const {
    id,
    created_at,
    title,
    subtitle,
    count,
    source,
    amount_inc_vat,
    state,
    department_name,
    organization_name,
    refund_transaction_id,
  } = transaction;

  const cost = state === 'refunded' ? -amount_inc_vat : amount_inc_vat;

  const ownerLabel = useMemo(() => {
    return department_name ? (
      <FormattedMessage defaultMessage="Avd" />
    ) : (
      <FormattedMessage defaultMessage="Org" />
    );
  }, [department_name]);

  const ownerName = department_name || organization_name;

  return (
    <div className="@container">
      <Button
        to="/$tenantId/reports/transactions/$transactionId"
        params={{ transactionId: id, tenantId: tenantId }}
        className="h-fit w-full flex-col items-start p-3 text-sm font-normal text-text-700 @2xl:grid @2xl:grid-cols-12 @2xl:items-center md:text-base"
      >
        <div className="flex w-full justify-between @2xl:col-span-2">
          <span>{formatDate(created_at)}</span>
          <span className="text-text-950 @2xl:hidden">
            {cost.toLocaleString(locale, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{' '}
            <FormattedMessage defaultMessage="SEK" />
          </span>
        </div>
        <div className="flex max-w-full flex-col text-start text-text-950 @2xl:col-span-5">
          <span>{count > 1 ? `${count}x ${title}` : title}</span>
          <span className="truncate">{subtitle}</span>
        </div>

        <div className="hidden min-w-0 flex-col items-start overflow-hidden @2xl:col-span-3 @2xl:flex">
          <p>{getSourceTranslation({ source }) ?? '-'}</p>
          <p className="w-full truncate">
            <span className="text-sm italic">{ownerLabel}:</span> {ownerName}
          </p>
        </div>

        <div className="hidden flex-col items-end @2xl:relative @2xl:col-span-2 @2xl:flex">
          <p className="text-text-950">
            {cost.toLocaleString(locale)}{' '}
            <FormattedMessage defaultMessage="SEK" />
          </p>
          <p>{getStateTranslation({ state }) ?? '-'}</p>
        </div>

        <div className="w-full min-w-0 overflow-hidden @2xl:hidden">
          <p className="truncate">
            <span className="text-sm italic">{ownerLabel}:</span> {ownerName}
          </p>
        </div>

        <div className="flex w-full justify-between @2xl:hidden">
          <span>{getSourceTranslation({ source }) ?? '-'}</span>
          <span>
            {getStateTranslation({ state }) ?? '-'}{' '}
            <span className="text-text-700">
              {refund_transaction_id
                ? `(${formatMessage({ defaultMessage: 'Återbetald' })})`
                : ''}
            </span>
          </span>
        </div>
      </Button>
    </div>
  );
};
